import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { BookmarkSimple, Plus, ArrowRight, Trash } from "@phosphor-icons/react";

export default function SavedICPs() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", plain_english: "" });
  const navigate = useNavigate();

  const load = async () => { const { data } = await api.get("/icps"); setItems(data.items || []); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    await api.post("/icps", form);
    toast.success("ICP saved");
    setShowNew(false); setForm({ name: "", description: "", plain_english: "" });
    load();
  };

  const applyIcp = async (id) => {
    const { data } = await api.post(`/icps/${id}/start-request`);
    toast.success("New draft created from ICP");
    navigate(`/requests/${data.request_id}`);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this ICP?")) return;
    await api.delete(`/icps/${id}`);
    load();
  };

  return (
    <div className="App min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">Saved audiences</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl md:text-5xl">Your ICPs.</h1>
            <p className="text-muted-foreground text-sm mt-2 max-w-lg">Save any audience profile you use repeatedly. Start a new request in one click.</p>
          </div>
          <Button data-testid="icp-new-btn" onClick={() => setShowNew(!showNew)} className="rounded-sm"><Plus size={14} className="mr-2" /> New ICP</Button>
        </div>

        {showNew && (
          <div className="border border-border bg-white p-6 mb-6 space-y-3">
            <div><Label>ICP name</Label><Input data-testid="icp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="US Enterprise IT Directors" /></div>
            <div><Label>Description (optional)</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <div><Label>Audience description (plain English)</Label><Textarea rows={4} value={form.plain_english} onChange={(e) => setForm({ ...form, plain_english: e.target.value })} className="mt-1 font-mono text-sm" placeholder="IT Directors and above at US manufacturers 500–5000 employees using SAP." /></div>
            <div className="flex gap-2">
              <Button data-testid="icp-save-btn" onClick={create} className="rounded-sm">Save ICP</Button>
              <Button variant="ghost" onClick={() => setShowNew(false)} className="rounded-sm">Cancel</Button>
            </div>
          </div>
        )}

        {items.length === 0 && !showNew && (
          <div className="border border-dashed border-border p-10 text-center">
            <BookmarkSimple size={28} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
            <div className="font-semibold mb-1">No saved ICPs yet</div>
            <p className="text-sm text-muted-foreground">Save your best audiences here for one-click reuse.</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {items.map((i) => (
            <div key={i.icp_id} className="border border-border bg-white p-5" data-testid={`icp-card-${i.icp_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-heading font-bold text-lg">{i.name}</div>
                  {i.description && <div className="text-xs text-muted-foreground">{i.description}</div>}
                </div>
                <button onClick={() => del(i.icp_id)} data-testid={`icp-delete-${i.icp_id}`} className="text-muted-foreground hover:text-destructive p-1" title="Delete">
                  <Trash size={16} />
                </button>
              </div>
              {i.plain_english && <p className="text-xs text-muted-foreground font-mono mt-2 line-clamp-3">{i.plain_english}</p>}
              <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
                <span>Used {i.times_used || 0}×</span>
                <Button size="sm" data-testid={`icp-use-${i.icp_id}`} onClick={() => applyIcp(i.icp_id)} className="rounded-sm h-8">
                  Start request <ArrowRight size={12} className="ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
